/** RESPONSIVE CURVE store
 * contain all data + logic to control our responsive ratios
 * 
 * then will use a vue cpn to render css vars in dom
 */
import { DEBUG } from 'config';
import { debounce } from 'lodash';
import { defineStore } from 'pinia';
import { pinia } from 'plugins/store/index';
import InterfaceStore from 'store/interface';
import { CustomEase, gsap } from 'vendors/gsap';
import { computed, nextTick, readonly, ref, toRef, watch } from 'vue';

const config = {
  // design viewport desktop
  desktop: 1440,
  // design viewport mobile
  mobile:375,
  // when we switch layout desktop/mobile
  layout:1000,
  // max grid ref when creating the svg...
  // @todo better...
  max:10000,
  // default path (svg) - this one is our best curve
  path: 'M0,10000s0-375.03,375-375c903.84-.02,56.55-290.64,1065-1065.14,723.37-555.55,2408.29-1365.28,6347.95-2471.16C9251.93,5677.75,10000,0,10000,0',
} as const

interface ResponsiveStore extends Obj {
  config: typeof config,
  ratios: {
    desktop: number,
    mobile: number,
  },
  path: string
}

const useStore = defineStore('Responsive', () => {
  // shortcuts
  const {
    desktop:DESKTOP,
    mobile: MOBILE,
    max:    MAX,
    path:   PATH,
  } = config
  const width = toRef(InterfaceStore.viewport, 'width')

  const state = ref<ResponsiveStore>({
    config,
    ratios: {
      desktop: DESKTOP / MAX,
      mobile: MOBILE / MAX,
    },
    path: PATH
  });


  // * ------------------------------------------------------------------------- Curve creation

  // Create our ease function only if not created, specific for storybook bug on story switch
  if (!CustomEase.get('responsive')) {
    CustomEase.create('responsive', state.value.path);
  }
  let useResponsiveEase = gsap.parseEase('responsive');
  const updateResponsiveEase = debounce((path)=>{
    CustomEase.create('responsive', path);
    useResponsiveEase = gsap.parseEase('responsive');
    state.value.path = path

    nextTick(()=>{
      ratioEased.value = useResponsiveEase(ratio.value)
    })
  }, 100)

  // * ------------------------------------------------------------------------- RATIOS

  /** compute base ratio (and eased ratio) :
   * > current vw from 0 to MAX
   */
  const ratio = computed(()=>width.value / MAX)
  const ratioEased = ref(useResponsiveEase(ratio.value))
  watch(()=>ratio.value, newRatio=>{
    ratioEased.value = useResponsiveEase(newRatio)
  })
  /** mobile / desktop related ratios
   * > v-mobile * ratio-mobile */
  const desktop = computed(()=>ratioEased.value / state.value.ratios.desktop)
  const mobile =  computed(()=>ratioEased.value / state.value.ratios.mobile)
  // just remember diff between ratios - we will multiply this to our diff ratio for a eased version
  const diffRatioEasedOrNot = computed(()=>ratioEased.value / ratio.value)
  const useRangeBetween = gsap.utils.mapRange(MOBILE, DESKTOP, 0,1)
  const rangeVw = computed(()=>useRangeBetween(width.value))
  // EASED RATIO BETWEEN MOBILE / DESKTOP from 0 to 1
  const between = computed(()=>rangeVw.value * diffRatioEasedOrNot.value)


  // * ------------------------------------------------------------------------- EXPORTS
  /** compute responsive value */
  function useResponsive(pxDesktop:number, pxMobile?:number, unit?:'px'|boolean){
    const value = (()=>{
      if (!pxMobile) {
        return pxDesktop * desktop.value
      }
      if (pxMobile && !pxDesktop) {
        return pxMobile * mobile.value
      }

      if(width.value > DESKTOP)
        return pxDesktop * desktop.value
      if(width.value < MOBILE)
        return pxMobile * mobile.value

      const diff = pxDesktop - pxMobile
      return pxMobile + (diff * between.value)
    })()

    return unit ? `${value}px` : value
  }

  return {
    state: readonly(state),
    path: computed(()=>state.value.path),
    
    // * ratios
    desktop,
    mobile,
    between,

    // * get responsive value
    useResponsive,
    r:useResponsive,

    // * internal stuff
    ratio,
    ratioEased,
    updateResponsiveEase,
  }
})
export const store = useStore(pinia);
export default store;

// @ts-expect-error
if(DEBUG) window.storeResponsive = store
